import { formatSlideCounter, getPreviewCount, getPreviewPosts } from "./utils";

function isTextOnlyStyle(block) {
	return (block.dataset.carouselStyle || "default") === "text-only";
}

function renderPreviewItem(post, isTextOnly = false) {
	const preview = document.createElement("button");
	preview.type = "button";
	preview.className = "sui-post-carousel-preview-card";
	preview.dataset.index = String(post.carouselIndex);
	preview.setAttribute("aria-label", `Show ${post.title}`);

	if (isTextOnly) {
		if (post.dateText) {
			const date = document.createElement("p");
			date.className = "sui-post-carousel-preview-date";
			date.textContent = post.dateText;
			preview.append(date);
		}

		const title = document.createElement("p");
		title.className = "sui-post-carousel-preview-title";
		title.textContent = post.title;

		const cta = document.createElement("div");
		cta.className = "sui-post-carousel-link";

		const label = document.createElement("span");
		label.textContent = post.buttonText || "Read more";

		const arrow = document.createElement("span");
		arrow.setAttribute("aria-hidden", "true");
		arrow.innerHTML = "&rarr;";

		cta.append(label, arrow);
		preview.append(title, cta);
		return preview;
	}

	const previewImage = document.createElement("div");
	previewImage.className = "sui-post-carousel-preview-image";

	if (post.image) {
		const image = document.createElement("img");
		image.src = post.image;
		image.alt = "";
		previewImage.append(image);
	} else {
		const placeholder = document.createElement("div");
		placeholder.className = "sui-post-carousel-preview-placeholder";
		previewImage.append(placeholder);
	}

	const title = document.createElement("p");
	title.className = "sui-post-carousel-preview-title";
	title.textContent = post.title;

	preview.append(previewImage, title);
	return preview;
}

function renderPost(block, state) {
	const currentPost = state.posts[state.activeIndex];

	if (!currentPost) {
		return;
	}

	const isTextOnly = isTextOnlyStyle(block);
	const previewCount = getPreviewCount(block.clientWidth);
	const previewPosts = getPreviewPosts(
		state.posts,
		state.activeIndex,
		previewCount,
	);
	const mediaLink = block.querySelector(".sui-post-carousel-media-link");
	const mediaVisual = block.querySelector(".sui-post-carousel-media-visual");
	const titleLink = block.querySelector(".sui-post-carousel-title-link");
	const excerpt = block.querySelector(".sui-post-carousel-excerpt");
	const date = block.querySelector(".sui-post-carousel-date");
	const author = block.querySelector(".sui-post-carousel-author");
	const previewContainer = block.querySelector(".sui-post-carousel-preview");
	const previewList = block.querySelector(".sui-post-carousel-preview-list");
	const categories = block.querySelectorAll(".sui-post-carousel-category");
	const counters = block.querySelectorAll(".sui-post-carousel-counter");
	const ctaLinks = block.querySelectorAll(
		".sui-post-carousel-content .sui-post-carousel-link",
	);

	block.dataset.activeIndex = String(state.activeIndex);

	if (!isTextOnly && mediaLink && mediaVisual) {
		mediaLink.href = currentPost.url || "#";
		mediaLink.setAttribute("aria-label", currentPost.title);
		mediaVisual.innerHTML = currentPost.image
			? `<img src="${currentPost.image}" alt="">`
			: '<div class="sui-post-carousel-media-placeholder"></div>';
	}

	if (titleLink) {
		titleLink.textContent = currentPost.title;
		titleLink.href = currentPost.url || "#";
	}

	if (excerpt) {
		excerpt.textContent = currentPost.excerpt || "";
		excerpt.classList.toggle("is-hidden", !currentPost.excerpt);
	}

	if (date) {
		date.textContent = currentPost.dateText || "";
		date.classList.toggle("is-hidden", !currentPost.dateText);
	}

	if (author) {
		author.textContent = currentPost.author || "";
		author.classList.toggle("is-hidden", !currentPost.author);
	}

	categories.forEach((category) => {
		category.textContent = currentPost.category || "";
		category.classList.toggle("is-hidden", !currentPost.category);
	});

	counters.forEach((counter) => {
		counter.textContent = formatSlideCounter(
			state.activeIndex,
			state.posts.length,
		);
	});

	ctaLinks.forEach((link) => {
		link.href = currentPost.url || "#";
		const label = link.querySelector("span");
		if (label) {
			label.textContent = currentPost.buttonText;
		}
	});

	if (previewContainer && previewList) {
		previewContainer.classList.toggle("is-hidden", previewPosts.length === 0);
		previewList.replaceChildren(
			...previewPosts.map((post) => renderPreviewItem(post, isTextOnly)),
		);
	}
}

function initializeBlock(block) {
	if (block.hasAttribute("data-initialized")) {
		return;
	}

	const dataNode = block.querySelector(".sui-post-carousel-data");

	if (!dataNode) {
		block.setAttribute("data-initialized", "true");
		return;
	}

	let posts = [];

	try {
		posts = JSON.parse(dataNode.textContent || "[]");
	} catch (error) {
		block.setAttribute("data-initialized", "true");
		return;
	}

	if (!posts.length) {
		block.setAttribute("data-initialized", "true");
		return;
	}

	const state = {
		activeIndex: Number(block.dataset.activeIndex || 0),
		isAnimating: false,
		posts,
	};

	const transitionTo = (nextIndex, direction) => {
		if (
			state.isAnimating ||
			nextIndex === state.activeIndex ||
			!state.posts[nextIndex]
		) {
			return;
		}

		state.isAnimating = true;
		block.dataset.direction = direction;
		block.classList.add("is-exiting");

		window.setTimeout(() => {
			state.activeIndex = nextIndex;
			renderPost(block, state);
			block.classList.remove("is-exiting");
			block.classList.add("is-entering");

			window.requestAnimationFrame(() => {
				window.requestAnimationFrame(() => {
					block.classList.remove("is-entering");
				});
			});

			window.setTimeout(() => {
				state.isAnimating = false;
			}, 260);
		}, 180);
	};

	block.querySelectorAll("[data-action='previous']").forEach((button) => {
		button.addEventListener("click", () => {
			transitionTo(
				(state.activeIndex - 1 + state.posts.length) % state.posts.length,
				"prev",
			);
		});
	});

	block.querySelectorAll("[data-action='next']").forEach((button) => {
		button.addEventListener("click", () => {
			transitionTo((state.activeIndex + 1) % state.posts.length, "next");
		});
	});

	const previewList = block.querySelector(".sui-post-carousel-preview-list");
	if (previewList) {
		previewList.addEventListener("click", (event) => {
			const target = event.target.closest(".sui-post-carousel-preview-card");

			if (!target) {
				return;
			}

			const nextIndex = Number(target.dataset.index || 0);
			const direction = nextIndex > state.activeIndex ? "next" : "prev";
			transitionTo(nextIndex, direction);
		});
	}

	window.addEventListener("resize", () => renderPost(block, state));
	block.setAttribute("data-initialized", "true");
	renderPost(block, state);
}

document.addEventListener("DOMContentLoaded", () => {
	document
		.querySelectorAll('[sui-block="sui/post-carousel"]')
		.forEach((block) => initializeBlock(block));
});
